import * as Accordion from '@radix-ui/react-accordion';
import { ampli } from '@/ampli';
import type { ConnectedWallet } from '@privy-io/react-auth';
import { useState, useContext } from 'react';

import { getCrossChainGasFee } from '@/lib/api/0x-cross-chain/gasCosts';
import type { CrossChainQuote } from '@/lib/api/0x-cross-chain/types';

import { isSankeyRouteAvailable } from '@/components/SankeyDiagram/utils';

import { solana } from '@/constants/chain';

import { MatchaConnectedWallet } from '@/hooks/useMatchaWallets';

import { useLiquiditySourcesStore } from '@/store/liquiditySources';

import { Price, Quote } from '@/utils/0x.types';
import { TokenResult } from '@/utils/0x/token-registry.types';
import { classNames } from '@/utils/classnames';
import { getLiquiditySources } from '@/utils/liquidity-sources';

import { SankeyContext } from '../../../SankeyDiagram/SankeyProvider';
import { BestRouteButton } from '../BestRouteButton';
import { CollapsedContentMarketMode, CollapsedContentCrossChainMode } from './CollapsedContent';
import { ExpandedContentMarketMode, ExpandedContentCrossChainMode } from './ExpandedContent';
import styles from './styles.module.css';

interface SwapDetailsAccordionMarketModeProps {
  currentPrice: Price | Quote | undefined;
  isWrap: boolean;
  isLoading: boolean;
  chainId: number;
  sellToken: TokenResult;
  buyToken: TokenResult;
  isCurrentSwapGasless: boolean;
  walletAddress: string | undefined;
  connectedWallet: MatchaConnectedWallet<ConnectedWallet> | undefined;
  nativeTokenUsd: number;
  tokenUsdPrices: { buyTokenUsd: number; sellTokenUsd: number };
}

export const SwapDetailsAccordionMarketMode = ({
  currentPrice,
  isWrap,
  isLoading,
  chainId,
  sellToken,
  buyToken,
  isCurrentSwapGasless,
  walletAddress,
  connectedWallet,
  nativeTokenUsd,
  tokenUsdPrices,
}: SwapDetailsAccordionMarketModeProps) => {
  const { setSankeyModalOpen } = useContext(SankeyContext);
  const [expandedItemId, setExpandedItemId] = useState<string>('');

  const liquiditySourcesAvailable = useLiquiditySourcesStore((state) => state.liquiditySources);
  const selectedLiquiditySourceCount =
    liquiditySourcesAvailable[chainId] !== undefined
      ? liquiditySourcesAvailable[chainId].selected
      : 1;
  const liquiditySources = currentPrice ? getLiquiditySources(currentPrice.route) : undefined;
  const isSankeyAvailable = isSankeyRouteAvailable(currentPrice, chainId);

  const solanaMaxSlippageBps =
    currentPrice && 'maxSlippageBps' in currentPrice ? currentPrice.maxSlippageBps : undefined;

  const toggleAccordion = (value: string) => {
    setExpandedItemId(value);
    ampli.feeInfoDropdownToggled({
      ui_state: value === 'swap-breakdown' ? 'opened' : 'closed',
      swap_order_type: 'market',
    });
  };

  const handleOnBestRouteButtonClick = () => {
    if (!isSankeyAvailable) return;
    setSankeyModalOpen(true);
    ampli.sankeyChartExpanded({
      ui_location: 'best_route_button',
    });
  };

  const bestRouteButton = (
    <BestRouteButton
      onClick={handleOnBestRouteButtonClick}
      loading={!currentPrice}
      disabled={!isSankeyAvailable}
      className="absolute my-auto top-1.5 left-4 h-fit w-fit disabled:cursor-default disabled:hover:bg-transparent disabled:active:bg-transparent"
      liquiditySources={liquiditySources || []}
    />
  );

  return (
    <div className="relative">
      {
        // only show the best route button if we have liquidity sources, we are on the preview and we are not expanded
        // Additionally, direct wraps are also displayed by the LiquiditySource component itself
        // NOTE: As `isLoading` is currently hooked up to the old price endpoints, we are temporarily using `currentPrice`
        // as an indication if the component is loading or not
        (!currentPrice || (currentPrice && liquiditySources)) &&
          expandedItemId === '' &&
          !isWrap &&
          bestRouteButton
      }
      <Accordion.Root
        type="single"
        collapsible
        value={expandedItemId}
        onValueChange={toggleAccordion}
      >
        <Accordion.Item value="swap-breakdown">
          <Accordion.Header>
            <Accordion.Trigger asChild>
              <button
                data-testid="swap-details-toggle"
                className="flex items-center justify-between h-fit px-6 w-full py-2.5 bg-transparent border-none hover:bg-token-alphalight-200 hover:cursor-pointer"
              >
                <span className={classNames('flex', 'flex-col', 'w-full', styles.animatedFadeIn)}>
                  <span className="flex items-center justify-between h-6 w-full">
                    <CollapsedContentMarketMode
                      mode={expandedItemId ? 'showLess' : 'showMore'}
                      isLoading={isLoading}
                      isWrap={isWrap}
                      isCurrentSwapGasless={isCurrentSwapGasless}
                      sellToken={sellToken}
                      buyToken={buyToken}
                      chainId={chainId}
                      currentPrice={currentPrice}
                      nativeTokenUsd={nativeTokenUsd}
                      tokenUsdPrices={tokenUsdPrices}
                    />
                  </span>
                </span>
              </button>
            </Accordion.Trigger>
          </Accordion.Header>

          <div className="w-full hidden relative bg-token-gray-300 min-h-px h-px [[data-state=open]_&]:block" />

          <Accordion.Content className={styles.accordionContent}>
            <div className="flex flex-col py-2 px-6 gap-1">
              <ExpandedContentMarketMode
                isLoading={isLoading}
                isWrap={isWrap}
                isCurrentSwapGasless={isCurrentSwapGasless}
                sellToken={sellToken}
                buyToken={buyToken}
                chainId={chainId}
                currentPrice={currentPrice}
                liquiditySources={liquiditySources}
                selectedLiquiditySourceCount={selectedLiquiditySourceCount}
                solanaMaxSlippageBps={solanaMaxSlippageBps}
                walletAddress={walletAddress}
                connectedWallet={connectedWallet}
                nativeTokenUsd={nativeTokenUsd}
                tokenUsdPrices={tokenUsdPrices}
              />
            </div>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion.Root>
    </div>
  );
};

interface SwapDetailsAccordionCrossChainModeProps {
  quote: CrossChainQuote;
  isLoading: boolean;
  sellToken: TokenResult | undefined;
  nativeTokenUsd: number;
  buyChainId?: number;
  sellChainId?: number;
  slippageBps: number;
  buySideConnectedWalletAddress?: string;
}

export const SwapDetailsAccordionCrossChainMode = ({
  quote: { gasCosts, fees, estimatedTimeSeconds, steps },
  isLoading,
  sellToken,
  nativeTokenUsd,
  buyChainId,
  sellChainId,
  slippageBps,
  buySideConnectedWalletAddress,
}: SwapDetailsAccordionCrossChainModeProps) => {
  const isSolanaPair = buyChainId === solana.id || sellChainId === solana.id;
  const showReceivingWalletLine = !!buyChainId && !isSolanaPair && !!buySideConnectedWalletAddress;
  const [expandedItemId, setExpandedItemId] = useState<string | undefined>(undefined);

  // Network cost = origin-chain gas + any bridge-side native fee. The 0x API
  // names the gas field differently per chain type (`totalNetworkFee` for EVM,
  // `total` for SVM), so read it via `getCrossChainGasFee` — reading a single
  // hard-coded field made every EVM-origin quote render as "$0.00". Some
  // bridges instead report all of the user-visible cost via `bridgeNativeFee`
  // with the gas field at "0"; summing the two covers both cases.
  const totalNetworkFee = (
    getCrossChainGasFee(gasCosts) + BigInt(fees.bridgeNativeFee?.amount ?? '0')
  ).toString();

  const toggleAccordion = (value: string) => {
    setExpandedItemId(value);
    ampli.feeInfoDropdownToggled({
      ui_state: value === 'swap-breakdown' ? 'opened' : 'closed',
      swap_order_type: 'cross_chain',
    });
  };

  return (
    <Accordion.Root
      type="single"
      collapsible
      value={expandedItemId}
      onValueChange={toggleAccordion}
    >
      <Accordion.Item value="swap-breakdown">
        <Accordion.Header>
          <Accordion.Trigger asChild>
            <div className="flex items-center justify-between h-fit px-6 w-full py-2.5 bg-transparent border-none hover:bg-token-alphalight-200 hover:cursor-pointer">
              <div className={classNames('flex', 'flex-col', 'w-full', styles.animatedFadeIn)}>
                <div className="flex items-center justify-between h-6 w-full">
                  <CollapsedContentCrossChainMode
                    mode={expandedItemId ? 'showLess' : 'showMore'}
                    isLoading={isLoading}
                    steps={steps ?? []}
                    totalNetworkFee={totalNetworkFee}
                    nativeTokenUsd={nativeTokenUsd}
                    chainType={gasCosts.chainType}
                  />
                </div>
              </div>
            </div>
          </Accordion.Trigger>
        </Accordion.Header>

        <div className="w-full hidden relative bg-token-gray-300 min-h-px h-px [[data-state=open]_&]:block" />

        <Accordion.Content className={styles.accordionContent}>
          <div className="flex flex-col py-2 px-6 gap-1">
            <ExpandedContentCrossChainMode
              isLoading={isLoading}
              sellToken={sellToken}
              steps={steps ?? []}
              totalNetworkFee={totalNetworkFee}
              estimatedTimeSeconds={estimatedTimeSeconds}
              nativeTokenUsd={nativeTokenUsd}
              chainType={gasCosts.chainType}
              buyChainId={buyChainId}
              slippageBps={slippageBps}
              buySideConnectedWalletAddress={buySideConnectedWalletAddress}
              showReceivingWalletLine={showReceivingWalletLine}
            />
          </div>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
};
